import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { CheckBox, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { getMaxCompareCards, isMobile } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';
import { CardCompareCheckboxProps } from './CardCompareCheckbox.types';
import { StyledCardCompareCheckbox } from './CardCompareCheckbox.styles';

/**
 * CardCompareCheckbox - Handles card comparison selection
 * Maximum 3 cards on desktop, 2 on mobile
 */
export const CardCompareCheckbox = memo<CardCompareCheckboxProps>(
  ({ cardCode, componentId, customClass, checkBoxLabel }) => {
    const { cobrandData, setCobrandData, setCompareTrayAlertVisible } = useStore(store => store);
    const [isChecked, setIsChecked] = useState(false);
    const isViewportL = useCheckBreakpoint('viewportL');
    const isMobileDevice = isMobile();

    // Sync checkbox state with store
    useEffect(() => {
      const checked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
      setIsChecked(checked);
    }, [cobrandData.selectedCards, cardCode]);

    // Keep over-limit checkboxes interactive enough to show the tray alert,
    // but visually indicate they cannot be newly selected.
    const isVisuallyDisabled = useMemo(() => {
      const isAlreadySelected = cobrandData.selectedCards.some(item => item.cardname === cardCode);
      const maxCards = getMaxCompareCards(isViewportL && !isMobileDevice);
      return cobrandData.selectedCards.length >= maxCards && !isAlreadySelected;
    }, [cobrandData.selectedCards, cardCode, isViewportL, isMobileDevice]);

    // Handle checkbox toggle
    const handleCheckboxChange = useCallback(() => {
      if (isVisuallyDisabled) {
        setCompareTrayAlertVisible(true);
        return;
      }

      setIsChecked(prev => {
        const newState = !prev;

        // Update selected cards array
        const updatedCards = newState
          ? [
              ...cobrandData.selectedCards,
              {
                cardname: cardCode,
                checked: newState,
              },
            ]
          : cobrandData.selectedCards.filter(card => card.cardname !== cardCode);

        setCobrandData({
          ...cobrandData,
          selectedCards: updatedCards,
        });

        return newState;
      });
    }, [cardCode, cobrandData, isVisuallyDisabled, setCobrandData, setCompareTrayAlertVisible]);

    return (
      <StyledCardCompareCheckbox
        className={clsx(customClass, isVisuallyDisabled && 'max-selection-reached')}
        data-component-name="m-cobrand-CardCompareCheckbox"
        data-testid="cobrand-CardCompareCheckbox"
        aria-disabled={isVisuallyDisabled}
      >
        <CheckBox
          checkboxLabel={checkBoxLabel}
          ariaLabel={`compare-${componentId}-checkbox`}
          onChange={handleCheckboxChange}
          checked={isChecked}
          checkboxName={`${componentId}-checkbox`}
          checkboxId={`${componentId}-checkbox`}
        />
      </StyledCardCompareCheckbox>
    );
  }
);

CardCompareCheckbox.displayName = 'CardCompareCheckbox';
